import React, {
  useCallback, useEffect, useId, useRef, useState,
} from 'react';
import { Loader2, Search, X } from 'lucide-react';
import client from '../lib/api';
import MediaThumb from './MediaThumb';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

const categoryLabels = {
  slot: 'Slot',
  'live-casino': 'Canlı Casino',
  crash: 'Crash',
};

/**
 * Tüm kategorilerde oyun arayan üst menü kutusu.
 *
 * Arama sunucuda yapılır (``/games/search``); sonuçlar ada göre en iyi
 * eşleşmeden başlayarak sıralanır ve seçilen oyun doğrudan açılır.
 */
const GameSearch = ({ onPlay, className = '' }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const requestId = useRef(0);
  const resultsId = useId();

  const term = q.trim();

  useEffect(() => {
    if (term.length < MIN_CHARS) {
      requestId.current += 1;
      setItems([]);
      setLoading(false);
      setActiveIndex(-1);
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      const id = requestId.current + 1;
      requestId.current = id;
      client.get('/games/search', { params: { q: term, limit: 8 } })
        .then(({ data }) => {
          if (id !== requestId.current) return;
          setItems(data.items || []);
          setActiveIndex(-1);
        })
        .catch(() => { if (id === requestId.current) setItems([]); })
        .finally(() => { if (id === requestId.current) setLoading(false); });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  // Dışarı tıklanınca sonuç listesi kapanır.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const select = useCallback((game) => {
    setOpen(false);
    setQ('');
    setItems([]);
    onPlay?.(game);
  }, [onPlay]);

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!items.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      select(items[activeIndex >= 0 ? activeIndex : 0]);
    }
  };

  const showPanel = open && term.length >= MIN_CHARS;

  return (
    <div ref={containerRef} className={`game-search relative ${className}`}>
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)] z-10 pointer-events-none" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="pusu-input pl-10 pr-9 h-11"
        placeholder="Oyun ara..."
        aria-label="Oyun ara"
        aria-autocomplete="list"
        aria-expanded={showPanel}
        aria-controls={resultsId}
        autoComplete="off"
      />
      {q && (
        <button
          type="button"
          onClick={() => { setQ(''); setItems([]); }}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white z-10"
          aria-label="Aramayı temizle"
        >
          <X size={15} />
        </button>
      )}

      {showPanel && (
        <div
          id={resultsId}
          role="listbox"
          className="game-search-panel absolute left-0 right-0 top-full mt-2 pusu-panel p-2 z-50 max-h-[70vh] overflow-y-auto"
        >
          {loading && (
            <div className="px-3 py-4 text-sm text-gray-400 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Aranıyor...
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-3 py-4 text-sm text-gray-400">
              “{term}” için sonuç bulunamadı.
            </div>
          )}
          {!loading && items.map((game, index) => (
            <button
              key={game.id}
              role="option"
              aria-selected={index === activeIndex}
              type="button"
              onClick={() => select(game)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`game-search-item w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left ${index === activeIndex ? 'is-active' : ''}`}
            >
              <MediaThumb
                src={game.image}
                className="w-11 h-11 rounded-lg object-cover shrink-0 bg-[var(--pusu-bg-2)]"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-white truncate">{game.name}</span>
                <span className="block text-[11px] text-gray-400 truncate">{game.provider}</span>
              </span>
              <span className="text-[10px] font-bold text-[var(--pusu-accent-light)] shrink-0">
                {categoryLabels[game.category] || ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GameSearch;
